import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import type {
  ComplianceFrameworksResponse,
  ComplianceAssessmentResponse,
  ComplianceGapsResponse,
  ComplianceFrameworkReportResponse,
} from "@/types/compliance";

export function useComplianceFrameworks() {
  return useQuery<ComplianceFrameworksResponse>({
    queryKey: ["compliance", "frameworks"],
    queryFn: () =>
      apiGet<ComplianceFrameworksResponse>("/api/v1/compliance/frameworks"),
    staleTime: 300_000, // Frameworks don't change often
  });
}

export function useComplianceAssessment(
  frameworkId: string | null,
  periodStart: string,
  periodEnd: string,
) {
  return useQuery<ComplianceAssessmentResponse>({
    queryKey: ["compliance", "assessment", frameworkId, periodStart, periodEnd],
    queryFn: () =>
      apiGet<ComplianceAssessmentResponse>(
        `/api/v1/compliance/frameworks/${frameworkId}/assessment`,
        { period_start: periodStart, period_end: periodEnd },
      ),
    enabled: !!frameworkId && !!periodStart && !!periodEnd,
  });
}

export function useComplianceGaps(
  frameworkId: string | null,
  periodStart: string,
  periodEnd: string,
) {
  return useQuery<ComplianceGapsResponse>({
    queryKey: ["compliance", "gaps", frameworkId, periodStart, periodEnd],
    queryFn: () =>
      apiGet<ComplianceGapsResponse>(
        `/api/v1/compliance/frameworks/${frameworkId}/gaps`,
        { period_start: periodStart, period_end: periodEnd },
      ),
    enabled: !!frameworkId && !!periodStart && !!periodEnd,
  });
}

export function useComplianceFrameworkReport(
  frameworkId: string | null,
  periodStart: string,
  periodEnd: string,
) {
  return useQuery<ComplianceFrameworkReportResponse>({
    queryKey: [
      "compliance",
      "framework-report",
      frameworkId,
      periodStart,
      periodEnd,
    ],
    queryFn: () =>
      apiGet<ComplianceFrameworkReportResponse>(
        `/api/v1/compliance/frameworks/${frameworkId}/report`,
        { period_start: periodStart, period_end: periodEnd },
      ),
    enabled: false, // Only fetch on demand
  });
}
